/**
 * What an employee submitted about themselves, laid out — for HR, and for the employee.
 *
 * ── Why this is shared and not two renderings ─────────────────────────────────────────────
 *
 * Settings → Employee Information shows HR anybody's submission; My Information shows a person
 * their own. Those are different pages with different permissions and exactly the same content:
 * the same eleven personal fields, the same masking over Aadhaar and the account number, the
 * same four repeating tables.
 *
 * One component, because the failure mode of two is silent. A field added to the HR view and
 * forgotten here would mean the company holds something about somebody that they cannot see, and
 * nothing would report it — it would simply be missing from a page nobody thinks to compare.
 *
 * ── Editing lives here too, and is off by default ─────────────────────────────────────────
 *
 * HR may correct what was submitted; an employee reads their own record and may not. So
 * `editing` is a prop rather than a second component, and My Information never passes it.
 *
 * ── Masking ───────────────────────────────────────────────────────────────────────────────
 *
 * Aadhaar and the bank account are masked until deliberately revealed, on BOTH pages. It is not
 * only about who is looking: the common task on either page is "has this been filled in", which
 * never needs the digits, and a screen that shows them by default shows them to whoever is
 * standing behind you.
 */

import React from 'react';
import { EmployeeSelfServiceProfile } from '../../types/employeeProfile';
import { maskAadhaar } from '../../utils/identityDocs';
import DocumentUploads from './DocumentUploads';
import './EmployeeProfileSections.css';

/** Last four digits only — same reasoning as maskAadhaar, applied to a bank account. */
export const maskAccount = (value?: string): string => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length < 4) return value ? '••••' : '—';
  return `••••••${digits.slice(-4)}`;
};

export const show = (value?: string | boolean): string => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const text = String(value ?? '').trim();
  return text === '' ? '—' : text;
};

/**
 * One label and its value.
 *
 * `wide` spans the whole grid row, for a value that reads as prose rather than as a datum — an
 * address, a joined list. Without it a full address sits in a third of a column and wraps to five
 * lines while the two columns beside it stay empty.
 */
export const Row: React.FC<{ label: string; children: React.ReactNode; wide?: boolean }> = ({
  label,
  children,
  wide,
}) => (
  <div className={wide ? 'eps__field eps__field--wide' : 'eps__field'}>
    <span className="eps__label">{label}</span>
    <span className="eps__value">{children}</span>
  </div>
);

export const Block: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="eps__section">
    <h3 className="eps__title">{title}</h3>
    <div className="eps__grid">{children}</div>
  </section>
);

/** A stable row id. crypto.randomUUID is unavailable on older Safari, hence the fallback. */
const rowId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * The repeating sections, declared once.
 *
 * Read and edit are generated from the SAME list, so a column cannot appear in the table and be
 * missing from the form — the exact divergence that made these sections read-only in the first
 * place, when the table was hand-written and no editor existed to match it.
 */
export const REPEATING_SECTIONS: {
  key: 'qualifications' | 'previousEmployment' | 'family';
  title: string;
  fields: { key: string; label: string; type?: string }[];
  /**
   * The label for this section's per-row attachments, when it takes them.
   *
   * Absent means the section has no documents — family details are names and dates and nothing
   * is ever filed against them. Declared here rather than checked at the render site so the
   * repeating sections stay generated from one list, which is the point of the list.
   */
  documentLabel?: string;
  /** The field to name a row by, so an attachment says which qualification it belongs to. */
  rowNameField?: string;
  /** At least one row, each with a document, is needed to submit. Shown as a * on the title. */
  required?: boolean;
}[] = [
  {
    key: 'qualifications',
    title: 'Qualifications',
    required: true,
    documentLabel: 'Certificate',
    rowNameField: 'qualification',
    fields: [
      { key: 'qualification', label: 'Qualification' },
      { key: 'institute', label: 'Institute' },
      { key: 'fromYear', label: 'From' },
      { key: 'toYear', label: 'To' },
      { key: 'grade', label: 'Grade' },
    ],
  },
  {
    key: 'previousEmployment',
    title: 'Previous employment',
    documentLabel: 'Relieving or experience letter',
    rowNameField: 'companyName',
    fields: [
      { key: 'companyName', label: 'Company' },
      { key: 'designation', label: 'Designation' },
      { key: 'fromDate', label: 'From', type: 'date' },
      { key: 'toDate', label: 'To', type: 'date' },
      { key: 'lastDrawnSalary', label: 'Last salary' },
      { key: 'reasonForLeaving', label: 'Reason' },
    ],
  },
  {
    key: 'family',
    title: 'Family details',
    fields: [
      { key: 'name', label: 'Name' },
      { key: 'relationship', label: 'Relationship' },
      { key: 'dateOfBirth', label: 'Date of birth', type: 'date' },
      { key: 'occupation', label: 'Occupation' },
    ],
  },
];

/**
 * A repeating section — a table to read, a table of inputs to edit.
 *
 * ── Rows are addressed by id, never by index ──────────────────────────────────────────────
 *
 * Every row carries a stable `id`, and edit and remove both match on it. Using the index would
 * work until somebody deleted a row above the one they were typing in, at which point every
 * subsequent edit would silently land on its neighbour — the same reasoning the schema gives for
 * addressing a change as "qualifications.<id>.institute" rather than by position.
 *
 * An added row gets an id immediately rather than on save, so React's keys are stable while the
 * form is being filled in and a half-typed row does not lose focus on every keystroke.
 */
const RepeatingBlock: React.FC<{
  title: string;
  fields: { key: string; label: string; type?: string }[];
  rows?: any[];
  editing: boolean;
  onChange: (rows: any[]) => void;
  /** Which storage folder this section's attachments go in. Absent means it takes none. */
  documentSection?: string;
  documentLabel?: string;
  rowNameField?: string;
  required?: boolean;
  employeeEmail: string;
}> = ({
  title,
  fields,
  rows,
  editing,
  onChange,
  documentSection,
  documentLabel,
  rowNameField,
  required = false,
  employeeEmail,
}) => {
  const list = rows || [];
  const heading = (
    <h3 className="eps__title">
      {title}
      {required && <span className="eps__req"> *</span>}
    </h3>
  );

  /**
   * The attachments for every row, under the table rather than as a column in it.
   *
   * A column would have to hold a filename, a View and a Remove inside a cell alongside six
   * others, on a table that already scrolls sideways on a laptop. Underneath, each row's files
   * get a full line and a heading that says which row they belong to — which is the thing a
   * reader needs, because "certificate.pdf" three times in a column tells them nothing.
   *
   * Rendered only for the rows that have something, unless HR is editing — then every row shows
   * its control, because an empty one is where you attach the file that is missing.
   */
  const attachments = documentSection
    ? list
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => editing || (row.documentKeys || []).length > 0)
        .map(({ row, index }) => {
          const named = String((rowNameField && row[rowNameField]) || '').trim();
          return (
          <DocumentUploads
            key={row.id || index}
            scope={{ employeeEmail }}
            section={documentSection}
            label={`${documentLabel} — ${named || `row ${index + 1}`}`}
            required={required}
            keys={row.documentKeys}
            onChange={
              editing
                ? (keys) =>
                    onChange(list.map((r) => (r.id === row.id ? { ...r, documentKeys: keys } : r)))
                : undefined
            }
          />
          );
        })
    : [];

  if (!editing) {
    return (
      <section className="eps__section">
        {heading}
        {list.length === 0 ? (
          <p className="eps__none">Nothing submitted.</p>
        ) : (
          <div className="eps__table-wrap">
            <table className="eps__table">
              <thead>
                <tr>
                  {fields.map((f) => (
                    <th key={f.key}>{f.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((row, index) => (
                  <tr key={row.id || index}>
                    {fields.map((f) => (
                      <td key={f.key}>{show(row[f.key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {attachments}
      </section>
    );
  }

  return (
    <section className="eps__section">
      {heading}

      {list.length > 0 && (
        <div className="eps__table-wrap">
          <table className="eps__table">
            <thead>
              <tr>
                {fields.map((f) => (
                  <th key={f.key}>{f.label}</th>
                ))}
                <th style={{ width: 1 }} />
              </tr>
            </thead>
            <tbody>
              {list.map((row, index) => (
                <tr key={row.id || index}>
                  {fields.map((f) => (
                    <td key={f.key}>
                      <input
                        className="eps__input"
                        type={f.type || 'text'}
                        value={row[f.key] ?? ''}
                        aria-label={`${title} row ${index + 1} ${f.label}`}
                        onChange={(event) =>
                          onChange(
                            list.map((r) =>
                              r.id === row.id ? { ...r, [f.key]: event.target.value } : r
                            )
                          )
                        }
                      />
                    </td>
                  ))}
                  <td>
                    <button
                      type="button"
                      className="eps__row-remove"
                      aria-label={`Remove ${title} row ${index + 1}`}
                      onClick={() => onChange(list.filter((r) => r.id !== row.id))}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        type="button"
        className="eps__add"
        onClick={() => onChange([...list, { id: rowId() }])}
      >
        + Add {title.toLowerCase().replace(/ details$/, '')}
      </button>

      {attachments}
    </section>
  );
};

/**
 * Read a dotted path out of the profile — "personal.dateOfBirth", "address.permanent.city".
 *
 * The same addressing EmployeeProfileChange.path uses, so what an input edits and what the
 * history records a change to are named identically on both sides of the wire.
 */
export const atPath = (source: any, path: string): string => {
  const value = path.split('.').reduce((node, key) => (node == null ? node : node[key]), source);
  return value === undefined || value === null ? '' : String(value);
};

/** The same path, written — copying each level rather than mutating the loaded profile. */
export const withPath = (source: any, path: string, value: any): any => {
  const [head, ...rest] = path.split('.');
  if (rest.length === 0) return { ...(source || {}), [head]: value };
  return { ...(source || {}), [head]: withPath((source || {})[head], rest.join('.'), value) };
};

/**
 * One field, shown or edited.
 *
 * Both states live in ONE component so a field cannot exist in the read view and be missing from
 * the edit form — which is how a screen ends up quietly refusing to correct the one column
 * somebody opened it for.
 */
const FieldRow: React.FC<{
  label: string;
  path: string;
  editing: boolean;
  draft: any;
  onChange: (path: string, value: string) => void;
  type?: string;
}> = ({ label, path, editing, draft, onChange, type = 'text' }) => (
  <div className="eps__field">
    <span className="eps__label">{label}</span>
    {editing ? (
      <input
        className="eps__input"
        type={type}
        value={atPath(draft, path)}
        onChange={(event) => onChange(path, event.target.value)}
      />
    ) : (
      <span className={atPath(draft, path) ? 'eps__value' : 'eps__value eps__value--empty'}>
        {show(atPath(draft, path))}
      </span>
    )}
  </div>
);

export interface ProfileSectionsProps {
  profile: EmployeeSelfServiceProfile;
  /** HR's working copy while editing; the stored profile otherwise. */
  source: any;
  editing?: boolean;
  /**
   * A dotted path and its new value.
   *
   * `value` is `any` rather than `string` because a section's `documentKeys` is an array and goes
   * through the same path writer. Widening the type here rather than adding a second callback
   * keeps one way to write into the draft — both hosts pass this straight to `withPath`, which
   * never cared what the value was.
   */
  onChange?: (path: string, value: any) => void;
  /**
   * A whole repeating section replaced — rows added, removed or edited.
   *
   * Separate from `onChange` because a row edit is not a path write: the rows carry ids, and the
   * caller stores the list as a unit. Squeezing both through one dotted-path callback would mean
   * inventing a path syntax for "the row whose id is X", which is exactly the addressing the
   * schema warns about getting subtly wrong.
   */
  onRowsChange?: (key: string, rows: any[]) => void;
  revealed: boolean;
  onToggleReveal: () => void;
}

const ProfileSections: React.FC<ProfileSectionsProps> = ({
  profile,
  source,
  editing = false,
  onChange = () => {},
  onRowsChange = () => {},
  revealed,
  onToggleReveal,
}) => {
  const permanent = profile.address?.permanent || {};
  const present = profile.address?.present || {};
  const ids = profile.identifications || {};
  const accounts = profile.accounts || {};

  const field = (label: string, path: string, type?: string) => (
    <FieldRow
      label={label}
      path={path}
      editing={editing}
      draft={source}
      onChange={onChange}
      type={type}
    />
  );

  return (
    <>
      <Block title="Personal information">
        {field('Date of birth', 'personal.dateOfBirth', 'date')}
        {field('Gender', 'personal.gender')}
        {field('Blood group', 'personal.bloodGroup')}
        {field('Mobile number', 'personal.mobileNumber')}
        {field('Personal email', 'personal.personalEmail', 'email')}
        {field("Father's name", 'personal.fathersName')}
        {field("Mother's name", 'personal.mothersName')}
        {field('Marital status', 'personal.maritalStatus')}
        {field('Spouse name', 'personal.spouseName')}
        {field('Marriage date', 'personal.marriageDate', 'date')}
        {field('Nationality', 'personal.nationality')}
      </Block>

      {/*
        * Address reads as one line and edits as its parts.
        *
        * Joining is right for reading — a reader scans an address, they do not audit its pincode
        * field — and useless for correcting, because there is no way to type a comma-joined
        * string back into seven stored fields without guessing at which comma meant what.
        */}
      <Block title="Address">
        {editing ? (
          <>
            {field('Permanent line 1', 'address.permanent.line1')}
            {field('Permanent line 2', 'address.permanent.line2')}
            {field('Permanent city', 'address.permanent.city')}
            {field('Permanent district', 'address.permanent.district')}
            {field('Permanent state', 'address.permanent.state')}
            {field('Permanent country', 'address.permanent.country')}
            {field('Permanent pincode', 'address.permanent.pincode')}
            {field('Present line 1', 'address.present.line1')}
            {field('Present city', 'address.present.city')}
            {field('Present state', 'address.present.state')}
            {field('Present pincode', 'address.present.pincode')}
          </>
        ) : (
          <>
            <Row label="Permanent" wide>
              {[permanent.line1, permanent.line2, permanent.city, permanent.district,
                permanent.state, permanent.country, permanent.pincode]
                .filter(Boolean)
                .join(', ') || '—'}
            </Row>
            <Row label="Present" wide>
              {profile.address?.presentSameAsPermanent
                ? 'Same as permanent'
                : [present.line1, present.city, present.state, present.pincode]
                    .filter(Boolean)
                    .join(', ') || '—'}
            </Row>
          </>
        )}
      </Block>

      <Block title="Emergency contact">
        {field('Name', 'emergencyContact.name')}
        {field('Mobile number', 'emergencyContact.mobileNumber')}
        {field('Relationship', 'emergencyContact.relationship')}
      </Block>

      {/*
        * Aadhaar and the account number edit UNMASKED, and only when revealed.
        *
        * An input prefilled with "••••••1234" would write those dots into the record the moment
        * somebody saved a different field in the same section. Requiring the reveal first also
        * keeps the masking meaningful: it is not a lock, but unmasking stays a deliberate act
        * rather than a side effect of pressing Edit.
        */}
      <Block title="Identifications">
        {revealed ? (
          field('Aadhaar', 'identifications.aadhaarNumber')
        ) : (
          <Row label="Aadhaar">
            {ids.aadhaarNumber ? maskAadhaar(ids.aadhaarNumber) || '••••' : '—'}
            {editing && (
              <span className="eps__hint">reveal to edit</span>
            )}
          </Row>
        )}
        {field('PAN', 'identifications.panNumber')}
        {field('Passport', 'identifications.passportNumber')}
        {field('Passport expiry', 'identifications.passportExpiry', 'date')}
        {field('Driving licence', 'identifications.drivingLicence')}

        {/*
          * The scans behind the numbers above.
          *
          * Not affected by `revealed`, which hides digits from somebody standing behind you —
          * a document has to be opened deliberately in a new tab, and it is already a signed URL
          * minted per click. Gating the list as well would only hide WHETHER a card is on file,
          * which is the one thing this page is most often opened to find out.
          */}
        <DocumentUploads
          scope={{ employeeEmail: profile.employeeEmail }}
          section="identifications"
          label="Aadhaar card"
          required
          keys={(source as any)?.identifications?.aadhaarDocumentKeys ?? ids.aadhaarDocumentKeys}
          onChange={editing ? (keys) => onChange('identifications.aadhaarDocumentKeys', keys) : undefined}
        />
        <DocumentUploads
          scope={{ employeeEmail: profile.employeeEmail }}
          section="identifications"
          label="PAN card"
          required
          keys={(source as any)?.identifications?.panDocumentKeys ?? ids.panDocumentKeys}
          onChange={editing ? (keys) => onChange('identifications.panDocumentKeys', keys) : undefined}
        />
        {/* Passport, driving licence, and any scans filed before Aadhaar and PAN had slots. */}
        <DocumentUploads
          scope={{ employeeEmail: profile.employeeEmail }}
          section="identifications"
          label="Other identity documents"
          keys={(source as any)?.identifications?.documentKeys ?? ids.documentKeys}
          onChange={editing ? (keys) => onChange('identifications.documentKeys', keys) : undefined}
        />
      </Block>

      <Block title="Accounts &amp; statutory">
        {field('Bank', 'accounts.bankName')}
        {field('Branch', 'accounts.branch')}
        {field('Name as per bank', 'accounts.nameAsPerBank')}
        {revealed ? (
          field('Account number', 'accounts.accountNumber')
        ) : (
          <Row label="Account number">
            {maskAccount(accounts.accountNumber)}
            {editing && (
              <span className="eps__hint">reveal to edit</span>
            )}
          </Row>
        )}
        {field('IFSC', 'accounts.ifscCode')}
        {field('PF number', 'accounts.pfNumber')}
        {field('UAN', 'accounts.uanNumber')}
        {field('ESI number', 'accounts.esiNumber')}

        <DocumentUploads
          scope={{ employeeEmail: profile.employeeEmail }}
          section="accounts"
          label="Bank documents"
          keys={(source as any)?.accounts?.documentKeys ?? accounts.documentKeys}
          onChange={editing ? (keys) => onChange('accounts.documentKeys', keys) : undefined}
        />
      </Block>

      {/*
        * One reveal for the whole page, not one per field.
        *
        * Per-field toggles make unmasking feel incidental. A single deliberate action is both
        * easier to reason about and easier to log later, if an access trail is wanted.
        */}
      <button
        type="button"
        onClick={onToggleReveal}
        className="eps__reveal"
      >
        {revealed ? 'Hide Aadhaar and account number' : 'Reveal Aadhaar and account number'}
      </button>

      {/*
        * Nomination details are deliberately NOT shown here.
        *
        * Removed on request. The stored `nominations` are untouched and the type still carries
        * them, so nothing is lost and bringing the section back is adding it to
        * REPEATING_SECTIONS — but the employee onboarding form still collects them, which is a
        * separate screen and a separate decision.
        */}
      {REPEATING_SECTIONS.map((section) => (
        <RepeatingBlock
          key={section.key}
          title={section.title}
          fields={section.fields}
          rows={(source as any)?.[section.key] ?? profile[section.key]}
          editing={editing}
          onChange={(rows) => onRowsChange(section.key, rows)}
          documentSection={section.documentLabel ? section.key : undefined}
          documentLabel={section.documentLabel}
          rowNameField={section.rowNameField}
          required={section.required}
          employeeEmail={profile.employeeEmail}
        />
      ))}
    </>
  );
};

export default ProfileSections;
